import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  Calendar as CalendarIcon,
  MapPin,
  ExternalLink,
  Navigation,
} from "lucide-react";
import Button from "@/components/ui/Button";
import { BackNavigationBar } from "@/components/ui/BackNavigationBar";
import { Card } from "@/components/ui/Card";
import { SpecialistMap } from "@/components/SpecialistMap";
import { SpecialistProfileSkeleton } from "./SpecialistProfileSkeleton";
import { BookingPanel, TELEMEDICINE_ID } from "./components/BookingPanel";
import { useSpecialistProfile } from "@/api/hooks/specialists.hooks";
import { useAvailableTimeSlots } from "@/api/hooks/appointment-slots.hooks";
import { formatDateKey } from "@/utils/date";
import { formatDoctorName } from "@/utils/specialist";
import type { AvailableTimeSlotsDto } from "@/api/contracts/appointment-slots.contract";

const CardSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Card className="border-gray-200 shadow-sm">
    <div className="p-4 sm:p-6 border-b border-gray-100">
      <h3 className="text-lg font-semibold text-gray-900 headline">{title}</h3>
    </div>
    <div className="p-4 sm:p-6">{children}</div>
  </Card>
);

const ListSection = ({ title, items }: { title: string; items: string[] }) => (
  <CardSection title={title}>
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 bg-primary rounded-full" />
          <span className="text-gray-600 body-copy">{item}</span>
        </li>
      ))}
    </ul>
  </CardSection>
);

export default function SpecialistProfile() {
  const navigate = useNavigate();
  const { specialistSlug, practiceSlug } = useParams({
    from: "/specialist-profile/$specialistSlug/$practiceSlug",
  });
  const {
    data: specialist,
    isLoading,
    error,
  } = useSpecialistProfile(specialistSlug, practiceSlug);

  const offersTelemedicine =
    specialist?.visitTypeAvailability?.toLowerCase().includes("telemedicine") ?? false;
  const defaultLocationId =
    specialist?.locationsInPerson?.[0]?.id ??
    (offersTelemedicine ? TELEMEDICINE_ID : undefined);

  const [pickedLocationId, setPickedLocationId] = useState<string | undefined>();
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedSlot, setSelectedSlot] = useState<AvailableTimeSlotsDto>();

  const selectedLocationId = pickedLocationId ?? defaultLocationId;
  const isTelemedicine = selectedLocationId === TELEMEDICINE_ID;
  const selectedLocation = isTelemedicine
    ? undefined
    : specialist?.locationsInPerson?.find((l) => l.id === selectedLocationId);

  const { data: rawSlots = [], isLoading: slotsLoading } = useAvailableTimeSlots({
    slug: specialist?.slug ?? "",
    locationId: isTelemedicine ? undefined : selectedLocationId,
  });
  const slots = isTelemedicine
    ? rawSlots.filter((s) => s.locationId === null)
    : rawSlots;
  const availableDates = [
    ...new Set(slots.map((s) => formatDateKey(s.dateTime))),
  ];
  const slotsForDate = selectedDate
    ? slots.filter(
        (s) => formatDateKey(s.dateTime) === formatDateKey(selectedDate),
      )
    : [];

  const handleBookNow = () => {
    if (!selectedSlot || !specialist) return;
    navigate({
      to: "/booking",
      search: {
        // Take the location from the slot rather than from the selector: the
        // API derives the visit type from the slot, so a slot published for
        // telemedicine must not be booked as in-person just because an
        // in-person location happens to be selected.
        specialistId: selectedSlot.specialistId,
        timeSlotId: selectedSlot.id,
        locationId: selectedSlot.locationId ?? undefined,
        locationAddress: selectedLocation?.address ?? undefined,
        appointmentDate: selectedSlot.dateTime,
        timeZone: selectedSlot.timeZone,
        doctor: formatDoctorName(specialist),
      },
    });
  };

  if (isLoading) return <SpecialistProfileSkeleton />;
  if (error || !specialist) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold mb-2 headline">
            Specialist Not Found
          </h1>
          <p className="text-muted-foreground mb-4 body-copy">
            Unable to load specialist profile information.
          </p>
          <Button onClick={() => navigate({ to: "/search" })}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Search
          </Button>
        </div>
      </div>
    );
  }

  const showLocation = !isTelemedicine && selectedLocation;
  const lat = selectedLocation?.latLong?.latitude;
  const lng = selectedLocation?.latLong?.longitude;
  const fullAddress = selectedLocation
    ? [selectedLocation.address, selectedLocation.city, selectedLocation.state, selectedLocation.zipCode]
        .filter(Boolean)
        .join(", ")
    : "";
  const shortAddress = selectedLocation
    ? [selectedLocation.city, selectedLocation.state].filter(Boolean).join(", ")
    : "";
  const directionsHref =
    selectedLocation?.directionUrl ??
    selectedLocation?.locationUrl ??
    (lat != null && lng != null
      ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
      : undefined);
  const initials = `${specialist.firstName?.[0] ?? ""}${specialist.lastName?.[0] ?? ""}`.toUpperCase();

  return (
    <div className="min-h-screen bg-background pb-20 font-sans">
      <BackNavigationBar
        rightContent={
          <Button
            onClick={() => document.getElementById("booking-card")?.scrollIntoView({ behavior: "smooth" })}
            className="lg:hidden bg-primary text-white hover:bg-primary/90"
            size="sm"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            Book Now
          </Button>
        }
      />

      <div className="container mx-auto px-4 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 sm:gap-8">
          <div className="lg:col-span-3 space-y-5 sm:space-y-8">
            <Card className="overflow-hidden border-gray-200 shadow-sm">
              <div className="p-5 sm:p-8">
                <div className="flex flex-row gap-4 sm:gap-6">
                  <div className="shrink-0">
                    {specialist.profilePictureUrl ? (
                      <img
                        src={specialist.profilePictureUrl}
                        alt={formatDoctorName(specialist)}
                        className="w-16 h-16 sm:w-24 sm:h-24 rounded-full object-cover border border-gray-100 shadow-inner bg-gray-50"
                      />
                    ) : (
                      <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                        <span className="text-xl sm:text-3xl font-bold">
                          {specialist.firstName?.[0]}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 flex flex-col sm:flex-row sm:justify-between gap-3 sm:gap-4">
                    <div className="min-w-0">
                      <span className="text-xl sm:text-3xl font-semibold text-gray-900 headline mb-1 block">
                        {formatDoctorName(specialist)}
                      </span>
                      {specialist.practiceName && (
                        <p className="body-copy text-base text-muted-foreground mb-3">
                          {specialist.practiceName}
                        </p>
                      )}
                      <div className="space-y-1.5">
                        {specialist.specialties && (
                          <p className="body-copy text-base sm:text-lg font-medium text-foreground">
                            {specialist.specialties}
                          </p>
                        )}
                        {specialist.visitTypeAvailability && (
                          <p className="body-copy text-sm sm:text-base text-muted-foreground">
                            Offers {specialist.visitTypeAvailability}
                          </p>
                        )}
                      </div>
                      {showLocation && (
                        <a
                          href={selectedLocation.locationUrl ?? undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-secondary hover:underline font-medium text-sm mt-4"
                        >
                          <MapPin className="h-4 w-4 shrink-0" />
                          <span className="md:hidden">{shortAddress}</span>
                          <span className="hidden md:inline">{fullAddress}</span>
                        </a>
                      )}
                    </div>
                    <div className="shrink-0 text-left sm:text-right">
                      <p className="text-lg sm:text-2xl font-bold text-foreground headline">
                        ${isTelemedicine ? specialist.telemedicinePricing : specialist.inPersonPricing}
                      </p>
                      <p className="body-copy text-xs text-muted-foreground">
                        Appointment cost
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {specialist.about && (
              <CardSection title={`About ${formatDoctorName(specialist)}`}>
                <p className="text-gray-600 leading-relaxed mb-6 body-copy whitespace-pre-line">
                  {specialist.about}
                </p>
                {specialist.practiceWebSite && (
                  <div className="flex items-center gap-2">
                    <ExternalLink className="h-4 w-4 text-secondary" />
                    <a
                      href={specialist.practiceWebSite}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-secondary hover:underline body-copy font-medium"
                    >
                      Visit Practice Website
                    </a>
                  </div>
                )}
              </CardSection>
            )}

            <div className="grid gap-6">
              {specialist.boardCertifications?.length ? (
                <ListSection
                  title="Board Certifications"
                  items={specialist.boardCertifications}
                />
              ) : null}
              {specialist.hospitalAffiliation?.length ? (
                <ListSection
                  title="Hospital Affiliations"
                  items={specialist.hospitalAffiliation}
                />
              ) : null}
            </div>

            {showLocation && (
              <CardSection title="Location">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  {selectedLocation.locationUrl ? (
                    <a
                      href={selectedLocation.locationUrl ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-secondary hover:underline"
                    >
                      {fullAddress}
                    </a>
                  ) : (
                    <span className="font-medium text-gray-900">{fullAddress}</span>
                  )}
                </div>
                {lat != null && lng != null && (
                  <div className="relative mt-4 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6">
                    <SpecialistMap
                      className="w-full h-[280px] sm:h-[360px]"
                      specialists={[
                        {
                          id: specialist.slug,
                          name: formatDoctorName(specialist),
                          lat,
                          lng,
                          specialty: specialist.specialties,
                          price: specialist.inPersonPricing,
                          address: fullAddress,
                          profilePictureUrl: specialist.profilePictureUrl ?? undefined,
                          initials: initials || undefined,
                        },
                      ]}
                      singleZoom={15}
                    />
                    {directionsHref && (
                      <a
                        href={directionsHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-xs font-semibold text-foreground shadow-md ring-1 ring-black/5 hover:bg-white transition-colors"
                      >
                        <Navigation className="h-3.5 w-3.5 text-secondary" />
                        Get directions
                      </a>
                    )}
                  </div>
                )}
              </CardSection>
            )}
          </div>

          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-24">
              <BookingPanel
                specialist={specialist}
                offersTelemedicine={offersTelemedicine}
                selectedLocationId={selectedLocationId}
                onLocationChange={(id) => {
                  setPickedLocationId(id);
                  setSelectedDate(undefined);
                  setSelectedSlot(undefined);
                }}
                isTelemedicine={isTelemedicine}
                selectedDate={selectedDate}
                onDateSelect={(d) => {
                  setSelectedDate(d);
                  setSelectedSlot(undefined);
                }}
                selectedSlot={selectedSlot}
                onSlotSelect={setSelectedSlot}
                availableDates={availableDates}
                slots={slotsForDate}
                slotsLoading={slotsLoading}
                onBookNow={handleBookNow}
              />
            </div>
          </div>
        </div>
      </div>

      <Button
        className="fixed bottom-6 right-6 z-50 lg:hidden shadow-2xl rounded-full w-16 h-16 p-0 bg-gradient-primary border-0 hover:scale-105 transition-transform"
        onClick={() => document.getElementById("booking-card")?.scrollIntoView({ behavior: "smooth" })}
      >
        <CalendarIcon className="h-8 w-8" />
      </Button>
    </div>
  );
}
